/**
 * parseReportingSections — partition the Report Card Language markdown
 * into collapsible cards for the Reporting tab of LessonWorkspace.
 *
 * The current LLM emits report-card stems grouped by strand:
 *   ### Curriculum Expectation Comments (…)
 *     **Excellent (E):** "…"
 *     **Good (G):** "…"
 *     **Satisfactory (S):** "…"
 *     **Needs Improvement (N):** "…"
 *   ### Transferable Skill Comments (…)
 *     …
 *   ### Learning Skill Comments (…)
 *     …
 *
 * Legacy lessons grouped by level instead (### Level 1/2/3/4). We support
 * both shapes; anything else falls back to flat rendering.
 *
 * @param {string} md
 * @returns {{
 *   intro: string,
 *   sections: Array<{id: string, label: string, body: string}>,
 *   style: "strand" | "level" | "flat",
 * }}
 */

export const REPORTING_LEVELS = [
  { id: "level3", label: "Level 3 / Good", pattern: /^(level\s*3|good\b|provincial standard)/i },
  { id: "level4", label: "Level 4 / Excellent", pattern: /^(level\s*4|excellent)/i },
  { id: "level2", label: "Level 2 / Satisfactory", pattern: /^(level\s*2|some\b|satisfactory|approaching)/i },
  { id: "level1", label: "Level 1 / Needs Improvement", pattern: /^(level\s*1|limited|needs improvement)/i },
];

export function parseReportingSections(md) {
  if (!md) return { intro: "", sections: [], style: "flat" };
  const lines = md.split("\n");
  // Collect candidate sub-headings: `### …`, `## …` (excluding the H2 wrapper
  // itself, e.g. `## Report Card Language`), or bolded "**Level N**" labels.
  const headings = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    let text = null;
    const h3 = line.match(/^#{3,4}\s*(.+?)\s*$/);
    const h2 = line.match(/^##\s*(.+?)\s*$/);
    const boldOnly = line.match(/^\*\*(.+?)\*\*\s*:?\s*$/);
    if (h3) text = h3[1].trim();
    else if (h2) {
      const t = h2[1].trim();
      if (/^report card language/i.test(t)) continue;
      text = t;
    } else if (boldOnly) {
      const t = boldOnly[1].trim();
      // Only treat bold-only lines as headings when they look like a Level
      // marker — bold lines like "**Excellent (E):**" stay in body text.
      if (/^level\s*[1-4]\b/i.test(t)) text = t;
    }
    if (text) headings.push({ index: i, text });
  }

  if (!headings.length) {
    return { intro: md.trim(), sections: [], style: "flat" };
  }

  const matchLevel = (text) => {
    for (const lvl of REPORTING_LEVELS) {
      if (lvl.pattern.test(text)) return lvl;
    }
    return null;
  };
  const anyLevel = headings.some((h) => matchLevel(h.text));
  const style = anyLevel ? "level" : "strand";
  const introRaw = lines.slice(0, headings[0].index).join("\n");
  // Filter the H2 wrapper from intro so we never render
  // "## Report Card Language" inside the tab that's already labelled Reporting.
  const intro = introRaw
    .split("\n")
    .filter((l) => !/^##\s*report card language/i.test(l.trim()))
    .join("\n")
    .trim();

  if (style === "level") {
    const byId = {};
    for (let h = 0; h < headings.length; h++) {
      const head = headings[h];
      const next = headings[h + 1];
      const end = next ? next.index : lines.length;
      const body = lines.slice(head.index, end).join("\n").trim();
      const lvl = matchLevel(head.text);
      if (!lvl) continue;
      byId[lvl.id] = (byId[lvl.id] ? byId[lvl.id] + "\n\n" : "") + body;
    }
    const sections = REPORTING_LEVELS
      .filter((lvl) => byId[lvl.id])
      .map((lvl) => ({ id: lvl.id, label: lvl.label, body: byId[lvl.id] }));
    return { intro, sections, style: sections.length ? "level" : "flat" };
  }

  // Strand mode: one card per heading. Strip the heading line from the body
  // so the card title isn't visually repeated inside the open panel.
  const sections = headings
    .map((head, idx) => {
      const next = headings[idx + 1];
      const end = next ? next.index : lines.length;
      const body = lines.slice(head.index + 1, end).join("\n").trim();
      return {
        id: `strand-${idx}`,
        label: head.text.replace(/\s*[—–-]\s*/g, " — ").trim(),
        body,
      };
    })
    .filter((s) => s.body);

  return { intro, sections, style: sections.length ? "strand" : "flat" };
}
